import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Heart, LoaderCircle } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getFavourites, createFavourite, deleteFavourite, getFavouriteByUrl } from "@/data-access/favourites";

interface FavoritesButtonProps {
  /** Whether the favorite functionality should be enabled */
  enabled?: boolean;
  /** Function to create default name for the favorite */
  createDefaultName?: (query: string) => string;
  /** Button size */
  size?: "sm" | "default" | "lg";
  /** Button variant */
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
}

const defaultCreateName = (query: string) => `Favorite: ${query}`;

export function FavoritesButton({
  enabled = true,
  createDefaultName = defaultCreateName,
  size = "sm",
  variant = "outline"
}: FavoritesButtonProps) {
  const queryClient = useQueryClient();
  
  // Get current query from the browser
  const searchParams = new URLSearchParams(window.location.search);
  const query = searchParams.get("query") || "";
  
  // State for favorites
  const [currentFavoriteId, setCurrentFavoriteId] = useState<number | null>(null);
  const [showFavoriteDialog, setShowFavoriteDialog] = useState(false);
  const [favoriteName, setFavoriteName] = useState("");

  // Helper function to get the most up-to-date current URL
  const getCurrentUrl = () => window.location.href;

  // Query to get favorites and check if current page is favorited
  const { data: favouritesData } = useQuery({
    queryKey: ["favourites", getCurrentUrl()],
    queryFn: () => getFavouriteByUrl(getCurrentUrl()),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Check if current page is in favorites
  useEffect(() => {
    if (favouritesData) {
      setCurrentFavoriteId(favouritesData.id);
    }
  }, [favouritesData, query, enabled]);

  // Mutations for favorite operations
  const createFavoriteMutation = useMutation({
    mutationFn: createFavourite,
    onSuccess: (data) => {
      setCurrentFavoriteId(data.favourite.id);
      queryClient.invalidateQueries({ queryKey: ["favourites"] });
    },
  });

  const deleteFavoriteMutation = useMutation({
    mutationFn: deleteFavourite,
    onSuccess: () => {
      setCurrentFavoriteId(null);
      queryClient.invalidateQueries({ queryKey: ["favourites"] });
    },
  });

  // Handle favorite button click
  const handleFavoriteClick = () => {
    if (!query || !enabled) return;

    if (currentFavoriteId) {
      // Remove from favorites
      deleteFavoriteMutation.mutate(currentFavoriteId);
    } else {
      // Show dialog to enter favorite name
      const defaultName = createDefaultName(query);
      setFavoriteName(defaultName);
      setShowFavoriteDialog(true);
    }
  };

  // Handle saving the favorite from dialog
  const handleSaveFavorite = () => {
    if (!favoriteName.trim()) return;
    
    createFavoriteMutation.mutate({
      url: getCurrentUrl(),
      name: favoriteName.trim(),
    });
    
    setShowFavoriteDialog(false);
    setFavoriteName("");
  };

  // Handle dialog cancel
  const handleCancelFavorite = () => {
    setShowFavoriteDialog(false);
    setFavoriteName("");
  };

  if (!enabled || !query) {
    return null;
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleFavoriteClick}
        disabled={createFavoriteMutation.isPending || deleteFavoriteMutation.isPending}
      >
        <Heart 
          className={`h-4 w-4 ${
            currentFavoriteId ? 'fill-current' : ''
          }`} 
        />
        {currentFavoriteId ? 'Remove Favorite' : 'Add Favorite'}
      </Button>

      {/* Add Favorite Dialog */}
      <Dialog open={showFavoriteDialog} onOpenChange={setShowFavoriteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add to Favorites</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="favorite-name">Name</Label>
              <Input
                id="favorite-name"
                value={favoriteName}
                onChange={(e) => setFavoriteName(e.target.value)}
                placeholder="Enter a name for this favorite"
                className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:transition-none bg-background border border-input h-12 rounded-lg text-foreground placeholder:text-muted-foreground w-full transition-all duration-200"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveFavorite();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelFavorite}
              disabled={createFavoriteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveFavorite}
              disabled={createFavoriteMutation.isPending || !favoriteName.trim()}
            >
              {createFavoriteMutation.isPending ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                'Add Favorite'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
} 